import { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from '@heroui/react';
import type { TipoPedido, TipoPedidoRequest } from '@/types/tipo-pedido.model';

interface TipoPedidoEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: string | number, data: TipoPedidoRequest['data']) => Promise<void>;
  loading?: boolean;
  tipoPedido?: TipoPedido;
}

const TipoPedidoEditModal = ({ isOpen, onClose, onSubmit, loading, tipoPedido }: TipoPedidoEditModalProps) => {
  const [formData, setFormData] = useState<Omit<TipoPedidoRequest['data'], 'tenantId' | 'locale' | 'localizations'>>({
    nombre: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form when tipoPedido changes
  useEffect(() => {
    if (tipoPedido) {
      setFormData({
        nombre: tipoPedido.nombre || '',
      });
    } else {
      setFormData({
        nombre: '',
      });
    }
  }, [tipoPedido]);

  const handleChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleSubmit = async () => {
    // Basic validation
    const newErrors: Record<string, string> = {}
    if (!formData.nombre.trim()) newErrors.nombre = 'Nombre es requerido';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (tipoPedido?.id) {
        await onSubmit(tipoPedido.id, formData);
        // Clear form data after successful submission
        setFormData({
          nombre: '',
        });
        // Clear any existing errors
        setErrors({});
        // Close the modal after successful submission
        onClose();
      }
    } catch (error) {
      // Optionally handle submission errors here
      console.error('Error submitting form:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e.target.name, e.target.value);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      placement="top-center"
      backdrop="blur"
      size="2xl"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Editar Tipo de Pedido</ModalHeader>
            <ModalBody className="space-y-4">
                <hr/>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  variant="bordered"
                  isRequired
                  errorMessage={errors.nombre}
                  className="md:col-span-2"
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button 
                color="danger" 
                variant="light" 
                onPress={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                color="primary" 
                onPress={handleSubmit}
                isLoading={loading}
              >
                Actualizar Tipo de Pedido
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default TipoPedidoEditModal;